import './EditPost.sass';

import React, {
  ClipboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';

import { Button, Header, ScrollView } from '$uikit';
import { createPostFormSelector, postsActions } from '$store/posts';
import { RootState } from '$store/rootReducer';
import { IcCloud28, IcGallery28, IcStatusSending16, IcVideo28 } from '$assets';
import { AttachPickerManagerRef } from 'shared/components/AttachPickerManager/AttachPickerManager.interface';
import AttachPickerManager from 'shared/components/AttachPickerManager/AttachPickerManager';
import { BottomBar } from '$uikit';
import {
  attachActions,
  attachItemsSelector,
  attachLinksSelector,
  attachPhotosSelector,
  attachTimerSelector,
} from '$store/attach';
import { usePost, useTranslator } from '$hooks';
import { Loader } from '$shared/components';
import { ATTACHMENTS_COUNT_LIMIT, classNames } from '$utils';
import { cloudActions } from '$store/cloud';
import { CLOUD } from '$shared/constants/pages';
import router from '$navigation/router';
import { useRoute } from 'react-router5';
import { mainSelector } from '$store/main';
import { goTo } from '$navigation/helper';
import { toastActions } from '$store/toast';

export function EditPost({ postId }: { postId: number }) {
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const dispatch = useDispatch();
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const pickerId = `edit_post_${postId}`;
  const timer = useSelector((state: RootState) => attachTimerSelector(state, pickerId));
  const post = usePost(postId);
  const isScheduled = post && post.createdAt * 1000 > Date.now();
  const t = useTranslator();
  const editor = useRef<ReactQuill>(null);
  const [isFocused, setFocused] = useState(false);
  const [rawText, setRawText] = useState('');
  const attachments = useSelector((state: RootState) =>
    attachItemsSelector(state, pickerId),
  );
  const { mVerified } = useSelector(mainSelector);
  const mediaAttachments = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );
  const link = useSelector((state: RootState) => attachLinksSelector(state, pickerId));
  const form = useSelector((state: RootState) => createPostFormSelector(state, pickerId));
  const { route } = useRoute();

  const mediaCount = mediaAttachments.length;
  const attachmentsLimit = ATTACHMENTS_COUNT_LIMIT;

  useEffect(() => {
    dispatch(postsActions.loadEditPost(postId));

    return () => {
      dispatch(postsActions.clearForm(pickerId));
    };
  }, []);

  const handleChange = (newVal) => {
    const contents = editor.current?.getEditor().getContents() ?? '';
    const rawText = editor.current?.getEditor().getText() || '';
    setRawText(rawText);
    dispatch(
      postsActions.updateFormText({
        formId: pickerId,
        text: post.level > 0 ? rawText : JSON.stringify(contents),
      }),
    );
    attachManager.current?.findLinks(newVal);
  };

  const handleSave = () => {
    if (attachments.length || rawText.trim()) {
      dispatch(postsActions.savePost(postId));
    }
  };

  const handlePhotoChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handleToggleTimer = useCallback(() => {
    attachManager.current?.toggleTimer();
  }, []);

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const { files } = event.clipboardData;

    if (files[0] && files[0].type.includes('image')) {
      attachManager.current?.uploadImages(files);
    }
  };

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const handleOnClickCheckAttachmentsLimit = (e) => {
    if (mediaCount >= attachmentsLimit) {
      dispatch(
        toastActions.setToastFail(t('edit_post_limit_error', { attachmentsLimit })),
      );
      return e.preventDefault();
    }
  };

  const defaultValue = useMemo(() => {
    if (!post) {
      return '';
    }

    const text = form.text || post.text;

    if (text.substring(0, 1) === '{') {
      try {
        return JSON.parse(text);
      } catch (e) {}
    }

    return text;
  }, [post]);

  if (!post) {
    return <Loader isWrapped />;
  }

  return (
    <>
      <Header showCloseButton>{t('edit_post_title')}</Header>
      <ScrollView>
        <div
          className="NewPost"
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {/* @ts-ignore */}
          <ReactQuill
            id="ql_editor"
            bounds=".NewPost"
            ref={editor}
            className={classNames({
              Editor: true,
              focused: isFocused,
            })}
            theme="bubble"
            placeholder={t('new_post_placeholder')}
            defaultValue={defaultValue}
            formats={[
              'header',
              'list',
              'bold',
              'italic',
              'strike',
              'underline',
              'link',
              'code',
            ]}
            onChange={handleChange}
            modules={{
              history: { delay: 1000, maxStack: 100, userOnly: false },
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['list', 'bold', 'italic', 'underline', 'strike', 'code'],
              ],
            }}
          />
          <AttachPickerManager ref={attachManager} pickerId={pickerId} />
          <div className="NewPost__attachments">
            {mVerified && (
              <div className="NewPost__attachments__photo_wrap">
                <Button
                  size="small"
                  mode="secondary"
                  icon={<IcCloud28 />}
                  onClick={() => {
                    if (mediaCount >= attachmentsLimit) {
                      dispatch(
                        toastActions.setToastFail(
                          t('edit_post_limit_error', { attachmentsLimit }),
                        ),
                      );
                      return;
                    }

                    dispatch(
                      cloudActions.setSavedContentActions([
                        attachActions.pushPhotos({
                          pickerId,
                          photos: mediaAttachments,
                        }),
                        attachActions.setTimer({ pickerId, time: timer }),
                        attachActions.setLink({ pickerId, link }),
                        postsActions.updateFormType({
                          formId: pickerId,
                          type: form.type,
                        }),
                        postsActions.updateFormText({
                          formId: pickerId,
                          text:
                            post.level > 0
                              ? rawText
                              : JSON.stringify(
                                  editor.current?.getEditor().getContents() ?? '',
                                ),
                        }),
                      ]),
                    );
                    dispatch(
                      cloudActions.setAttachParams({
                        pickerId,
                        routeName: route.name,
                        routeParams: route.params,
                      }),
                    );
                    goTo(
                      CLOUD,
                      {},
                      {
                        limit: attachmentsLimit - mediaCount,
                      },
                    );
                  }}
                >
                  {t('new_post_attach_cloud')}
                </Button>
              </div>
            )}
            <div className="NewPost__attachments__photo_wrap">
              <Button mode="secondary" icon={<IcGallery28 />} size="small">
                {t('new_post_attach_photo')}
              </Button>
              <input
                key={photoInputKey}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                onClick={handleOnClickCheckAttachmentsLimit}
                multiple
              />
            </div>
            <div className="NewPost__attachments__photo_wrap">
              <Button mode="secondary" icon={<IcVideo28 />} size="small">
                {t('new_post_attach_video')}
              </Button>
              <input
                key={videoInputKey}
                type="file"
                onChange={handleVideoChange}
                onClick={handleOnClickCheckAttachmentsLimit}
                accept="video/mp4,video/quicktime"
              />
            </div>
            {isScheduled && (
              <Button
                mode={timer ? 'light' : 'secondary'}
                size="small"
                icon={<IcStatusSending16 width={28} height={28} />}
                onClick={handleToggleTimer}
              >
                {t('new_post_attach_timer')}
              </Button>
            )}
          </div>
        </div>
      </ScrollView>
      <BottomBar>
        <Button onClick={handleSave} stretched>
          {t('save')}
        </Button>
      </BottomBar>
    </>
  );
}
